// Constants
import { JustificationReasonKeys } from "@ms/uno-constants/lib/local/ContentSensitivityLabelConstants";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Radio, RadioGroup, Text, Textarea } from "@fluentui/react-components";
import { Dismiss } from "@ms/planner-generic-controls/lib/local/icons";
import { SecureLink } from "@ms/planner-generic-controls/lib/local/secureLink/SecureLink";
// Styles
import { useSensitivityDialogStyles } from "./ContentSensitivityLabels.styles";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import * as React from "react";
/**
 * This control is invoked if changing the sensitivity label requires downgrade justification.
 * This control ensures to collect the user justification.
 */ export const SensitivityJustificationDialog = /*#__PURE__*/ React.memo((props)=>{
    const { open, moreInfoUrl, onCancelled, onSubmitted, sensitivityButtonRef } = props;
    const classes = useSensitivityDialogStyles();
    const [selectedJustification, setSelectedJustification] = React.useState(null);
    const [otherJustificationText, setOtherJustificationText] = React.useState("");
    const disableJustificationText = selectedJustification !== JustificationReasonKeys.Other;
    const disableChangeButton = !selectedJustification || !disableJustificationText && otherJustificationText.length === 0;
    const onJustificationChange = (_ev, option)=>{
        const justificationReasonKey = option.value;
        setSelectedJustification(justificationReasonKey);
        if (justificationReasonKey !== JustificationReasonKeys.Other) {
            setOtherJustificationText("");
        }
    };
    const onChangeOtherTextValue = (_event, data)=>{
        setOtherJustificationText(data.value);
    };
    const resetState = ()=>{
        setSelectedJustification(null);
        setOtherJustificationText("");
    };
    const onChangeClicked = async ()=>{
        // Build justification string based on selection
        let justificationText = selectedJustification;
        switch(selectedJustification){
            case JustificationReasonKeys.NotApplicable:
                justificationText = Strings.ContentSensitivityLabel_JustificationDialogLabelNotApplicableJustificationReasonLabel;
                break;
            case JustificationReasonKeys.Incorrect:
                justificationText = Strings.ContentSensitivityLabel_JustificationDialogLabelIncorrectJustificationReasonLabel;
                break;
            default:
                justificationText = otherJustificationText;
        }
        onSubmitted(justificationText);
        // Reset state after submission
        resetState();
    };
    const onCancelOrDismissClicked = ()=>{
        resetState();
        sensitivityButtonRef?.current?.focus();
        onCancelled();
    };
    const handleDialogOpenChange = (_ev, data)=>{
        if (!data.open) {
            onCancelOrDismissClicked();
        }
    };
    const onKeyDown = (ev)=>{
        // Prevent the event from bubbling up to the parent element that handles the keydown event
        ev.stopPropagation();
    };
    return /*#__PURE__*/ React.createElement(Dialog, {
        open: open,
        onOpenChange: handleDialogOpenChange
    }, /*#__PURE__*/ React.createElement(DialogSurface, {
        className: classes.dialog,
        onKeyDown: onKeyDown,
        "data-testid": "justification-dialog"
    }, /*#__PURE__*/ React.createElement(DialogBody, null, /*#__PURE__*/ React.createElement(DialogTitle, {
        action: /*#__PURE__*/ React.createElement(Button, {
            onClick: onCancelOrDismissClicked,
            appearance: "subtle",
            icon: /*#__PURE__*/ React.createElement(Dismiss, null),
            "aria-label": Strings.SharedComponents_CloseButtonText,
            "data-testid": "justification-dialog-close-button"
        })
    }, Strings.ContentSensitivityLabel_JustificationDialogTitle), /*#__PURE__*/ React.createElement(DialogContent, {
        className: classes.dialogContent
    }, /*#__PURE__*/ React.createElement("div", {
        className: classes.messageContainer
    }, /*#__PURE__*/ React.createElement(Text, {
        "data-testid": "justification-dialog-message"
    }, Strings.ContentSensitivityLabel_JustificationDialogMessage, " ", moreInfoUrl && /*#__PURE__*/ React.createElement(SecureLink, {
        href: moreInfoUrl,
        "data-testid": "justification-dialog-learn-more-link"
    }, Strings.ContentSensitivityLabel_JustificationDialogLearnMoreText))), /*#__PURE__*/ React.createElement("div", {
        className: classes.inputContainer
    }, /*#__PURE__*/ React.createElement(RadioGroup, {
        className: classes.radioGroup,
        onChange: onJustificationChange,
        "data-testid": "justification-dialog-radio-group"
    }, /*#__PURE__*/ React.createElement(Radio, {
        value: JustificationReasonKeys.NotApplicable,
        label: Strings.ContentSensitivityLabel_JustificationDialogLabelNotApplicableJustificationReasonLabel,
        "data-testid": "justification-radio-not-applicable"
    }), /*#__PURE__*/ React.createElement(Radio, {
        value: JustificationReasonKeys.Incorrect,
        label: Strings.ContentSensitivityLabel_JustificationDialogLabelIncorrectJustificationReasonLabel,
        "data-testid": "justification-radio-incorrect"
    }), /*#__PURE__*/ React.createElement(Radio, {
        value: JustificationReasonKeys.Other,
        label: Strings.ContentSensitivityLabel_JustificationDialogOtherJustificationReasonLabel,
        "data-testid": "justification-radio-other"
    })), /*#__PURE__*/ React.createElement(Textarea, {
        placeholder: Strings.ContentSensitivityLabel_JustificationDialogOtherJustificationReasonPlaceholder,
        onChange: onChangeOtherTextValue,
        value: otherJustificationText,
        disabled: disableJustificationText,
        className: classes.textInput,
        "data-testid": "justification-dialog-textarea"
    }))), /*#__PURE__*/ React.createElement(DialogActions, null, /*#__PURE__*/ React.createElement(Button, {
        onClick: onCancelOrDismissClicked,
        "data-testid": "justification-dialog-cancel-button"
    }, Strings.Common_CancelText), /*#__PURE__*/ React.createElement(Button, {
        appearance: "primary",
        onClick: onChangeClicked,
        disabled: disableChangeButton,
        "data-testid": "justification-dialog-change-button"
    }, Strings.Common_ChangeText)))));
});
